import { RightOutlined } from '@ant-design/icons';
import { Card, Flex, Skeleton, theme, Tooltip, Typography } from 'antd';
import type { ResumoDasPessoas } from '@titularis/api-client';
import { emReais } from '../../dominio';
import {
  comConversao,
  DEGRAU_AJUDA,
  DEGRAU_LABEL,
  foraDaEscada,
  formatarTaxa,
} from './vocabulario';

/**
 * O funil, de cima para baixo, em uma faixa.
 *
 * **É a resposta que a seção existe para dar.** No dia em que o GTM começou
 * havia doze contas e nenhuma forma de saber quantas tinham confirmado o
 * e-mail, respondido uma questão ou chegado ao checkout - as três telas
 * anteriores contavam gente por outro eixo (papel, direito, canal), e nenhuma
 * contava por *etapa*.
 *
 * **Cartão, e não gráfico de barras.** Com uma dúzia de pessoas, um funil
 * desenhado em proporção é uma barra cheia seguida de seis fatias invisíveis; o
 * número lido é exato em qualquer escala. O que o desenho carrega é a
 * **passagem** entre os degraus, e ela vai na seta - é ali que mora a decisão.
 *
 * **Os degraus não são clicáveis, e isso é decisão e não falta.** "Estudaram" e
 * a etapa "Estudando" parecem o mesmo filtro e não são: o degrau conta quem já
 * fez alguma coisa **alguma vez**, incluindo quem depois comprou; a etapa conta
 * onde a pessoa está **hoje**. Clicar em "Estudaram: 6" e receber três linhas
 * seria a tela se contradizendo em dois centímetros de distância. Quem recorta
 * é o seletor de etapa, logo abaixo, onde as cinco etapas são as mesmas da
 * coluna.
 */
export function FunilDasPessoas({
  resumo,
  carregando,
}: {
  resumo: ResumoDasPessoas | undefined;
  carregando: boolean;
}) {
  const { token } = theme.useToken();

  if (carregando && !resumo) {
    return (
      <Card size="small">
        <Skeleton active paragraph={{ rows: 2 }} />
      </Card>
    );
  }

  if (!resumo) return null;

  const degraus = comConversao(resumo.degraus);

  return (
    <Card
      size="small"
      styles={{
        body: { paddingBlock: token.paddingSM },
        /*
         * O cabeçalho pequeno do Card não tem respiro vertical, só altura
         * mínima. Numa linha ele não precisa; quando o resumo quebra no
         * celular, o título encostava na borda e a última linha no separador.
         */
        header: { paddingBlock: token.paddingXS },
      }}
      title={
        <Flex justify="space-between" align="center" wrap gap={token.marginXS}>
          <Typography.Text strong>A jornada, degrau a degrau</Typography.Text>
          {/*
            `whiteSpace: normal` porque o título do Card é `nowrap` com
            `overflow: hidden`, e a linha herdava as duas coisas: no celular ela
            tem uns 450px numa caixa de 318, e "contas internas fora do funil"
            sumia cortado no meio da palavra, sem reticências.
          */}
          <Typography.Text type="secondary" style={{ fontWeight: 'normal', whiteSpace: 'normal' }}>
            {emReais(resumo.centavos)} em compras pagas
            {resumo.suspensas > 0 ? ` · ${resumo.suspensas} conta(s) suspensa(s)` : null}
            {/*
              O que o funil deixou de fora, dito em voz alta. Sem isto, quem
              conta as linhas da lista com as internas à vista acha um número
              maior que "Criaram conta" e conclui que o funil está errado.
            */}
            {resumo.internas > 0 ? (
              <Tooltip title="Contas da equipe e contas marcadas como teste. Não entram em degrau nenhum.">
                <span>{` · ${resumo.internas} conta(s) interna(s) fora do funil`}</span>
              </Tooltip>
            ) : null}
          </Typography.Text>
        </Flex>
      }
    >
      {/*
        Rola na horizontal em vez de quebrar em duas fileiras: um funil que
        dobra de linha deixa de ser lido como sequência, e é a sequência que
        carrega o significado. No celular a faixa desliza.
      */}
      <Flex
        align="center"
        gap={token.marginXXS}
        style={{ overflowX: 'auto', paddingBottom: token.paddingXXS }}
      >
        {degraus.map((degrau, indice) => {
          const taxa = formatarTaxa(degrau.conversao);
          const solto = foraDaEscada(degrau.etapa);

          return (
            <Flex key={degrau.etapa} align="center" gap={token.marginXXS}>
              {/*
                A seta liga um degrau ao seguinte, então ela só existe **dentro
                da escada**. Quem fica de fora - a lista de espera e o retrato de
                quem está no limite - aparece separado por um espaço, sem seta:
                é o desenho dizendo que aquele número não se divide pelo vizinho.
              */}
              {indice > 0 ? (
                <Flex vertical align="center" style={{ minWidth: 44 }}>
                  {solto || foraDaEscada(degraus[indice - 1]!.etapa) ? null : (
                    <RightOutlined style={{ color: token.colorTextQuaternary, fontSize: 12 }} />
                  )}
                  {/*
                    A taxa fica **na seta**, e não dentro do cartão: ela não
                    descreve o degrau, descreve a passagem para ele. Dentro do
                    cartão, pareceria um segundo número sobre a mesma coisa.
                  */}
                  {taxa ? (
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      {taxa}
                    </Typography.Text>
                  ) : null}
                </Flex>
              ) : null}

              <Tooltip title={DEGRAU_AJUDA[degrau.etapa]}>
                <Flex
                  vertical
                  gap={2}
                  style={{
                    minWidth: 118,
                    paddingBlock: token.paddingXS,
                    paddingInline: token.paddingSM,
                    borderRadius: token.borderRadius,
                    /*
                      Quem está fora da escada fica com a borda tracejada: é a
                      diferença entre "por aqui passaram N" e "aqui estão N
                      agora", dita sem uma linha de texto a mais.
                    */
                    border: `1px ${solto ? 'dashed' : 'solid'} ${token.colorSplit}`,
                    background: solto ? 'transparent' : token.colorFillQuaternary,
                  }}
                >
                  <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {DEGRAU_LABEL[degrau.etapa]}
                  </Typography.Text>
                  <Typography.Text strong style={{ fontSize: 22, lineHeight: 1.1 }}>
                    {degrau.total}
                  </Typography.Text>
                </Flex>
              </Tooltip>
            </Flex>
          );
        })}
      </Flex>
    </Card>
  );
}
